import type { FC } from 'react'
import { PatientUser } from '~/shared/types'
import { Button, Grid, TextField, Autocomplete, type TextFieldProps } from '@mui/material'
import { useFormik } from 'formik'

import type { StatesListResponse } from '@fireflyhealth/api'

import { EditField } from '~/shared/ui'
import { useStates } from '~/entities/state'

interface EditAddressFormProps {
  patient: PatientUser
  loading: boolean
  handleSubmit: (values: PatientUser) => void | Promise<void>
}

const EditAddressForm: FC<EditAddressFormProps> = props => {
  const { data: states, isPending: statesLoading } = useStates()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: props.patient,
    onSubmit: props.handleSubmit,
  })

  const {
    person: { insurance_info: insuranceInfo },
  } = formik.values

  // Shortcut for change handlers and values
  const setValues = (name: string, value: TextFieldProps['value']) => ({
    name,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist()
      formik.handleChange(e)
      void formik.setFieldTouched(name, true, false)
    },
    disabled: props.loading,
    value,
  })

  const firstAddress = insuranceInfo?.addresses?.[0]

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <EditField
          {...setValues(
            'person.insurance_info.addresses[0].street_address',
            firstAddress?.street_address,
          )}
          label="Street Address"
        />
        <EditField
          {...setValues(
            'person.insurance_info.addresses[0].street_address_2',
            firstAddress?.street_address_2,
          )}
          label="Apt. or Unit #"
        />
        <EditField
          {...setValues('person.insurance_info.addresses[0].city', firstAddress?.city)}
          label="City"
        />
        <Grid size={6}>
          <Autocomplete
            options={states?.filter(state => state.abbreviation) ?? []}
            getOptionLabel={option =>
              typeof option === 'string' ? option : option.abbreviation || ''
            }
            value={
              // Find matching state object or create a temporary one for current value
              states?.find(state => state.abbreviation === firstAddress?.state) ??
              (firstAddress?.state
                ? ({
                    id: -1,
                    abbreviation: firstAddress?.state,
                    name: firstAddress?.state,
                  } as StatesListResponse[number])
                : null)
            }
            onChange={(_, newValue) => {
              const value = typeof newValue === 'string' ? newValue : newValue?.abbreviation || ''
              void formik.setFieldValue('person.insurance_info.addresses[0].state', value)
              void formik.setFieldTouched('person.insurance_info.addresses[0].state', true, false)
            }}
            disabled={props.loading || statesLoading}
            freeSolo
            renderInput={params => (
              <TextField
                {...params}
                label="State"
                variant="standard"
                disabled={props.loading || statesLoading}
              />
            )}
          />
        </Grid>
        <EditField
          {...setValues('person.insurance_info.addresses[0].zipcode', firstAddress?.zipcode)}
          label="Zip Code"
        />
        <EditField value={insuranceInfo?.latitude} label="Latitude" disabled />
        <EditField value={insuranceInfo?.longitude} label="Longitude" disabled />
      </Grid>
      <Grid container spacing={1} sx={{ justifyContent: 'flex-end' }}>
        {formik.dirty && (
          <Grid>
            <Button onClick={() => formik.resetForm()}>Cancel Address Changes</Button>
          </Grid>
        )}
        <Grid>
          <Button type="submit" color="primary" variant="outlined">
            Save Address
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default EditAddressForm
